import { useMemo } from 'react'

import styled from 'styled-components/macro'

import { formatDate, formatDateRange } from 'utils/dates'
import { formatNameWithCode } from 'utils/formatters'

import { EMPTY_CELL_CONTENT } from 'shop/products/utils'

import {
  OrderDetailsQuery,
  OrderDetailsTestIds,
} from '../../OrderDetailsWidget'
import { OrderDetailId } from '../../orderDetailsWidget.types'
import { DetailsListItem } from 'features/Orders/components'

export default function OrderDetailsViewList({
  details,
  isBrand,
  dateFormat,
}: Props) {
  const viewModeDetailList = useMemo(() => {
    let orderDeliveryWindowContent = formatDateRange(
      details?.collections?.deliveryWindow?.minStartShipDate || '',
      details?.collections?.deliveryWindow?.maxCompleteShipDate || '',
      { dateFormat, utc: true },
    )

    if (!details?.collections?.edges.length) {
      orderDeliveryWindowContent = EMPTY_CELL_CONTENT
    } else if (
      !details?.collections?.deliveryWindow?.minStartShipDate &&
      !details?.collections?.deliveryWindow?.maxCompleteShipDate
    ) {
      orderDeliveryWindowContent = 'Immediate'
    }

    const completeDetailsList = details
      ? [
          {
            id: OrderDetailId.OrderCreatedDate,
            title: 'Order Created Date',
            testId: OrderDetailsTestIds.OrderCreatedDateItem,
            content: formatDate(details?.created),
          },
          {
            id: OrderDetailId.Season,
            title: 'Season',
            testId: OrderDetailsTestIds.SeasonItem,
            content: details?.season
              ? formatNameWithCode(details.season.name, details.season.code)
              : EMPTY_CELL_CONTENT,
          },
          {
            id: OrderDetailId.Year,
            testId: OrderDetailsTestIds.YearItem,
            title: 'Year',
            content: details?.season?.year || EMPTY_CELL_CONTENT,
          },
          {
            id: OrderDetailId.Warehouse,
            title: 'Warehouse',
            testId: OrderDetailsTestIds.WarehouseItem,
            content: details?.warehouses
              ? details?.warehouses?.edges
                  .map((edge) =>
                    formatNameWithCode(
                      edge?.node?.name || '',
                      edge?.node?.code,
                    ),
                  )
                  .join(',')
              : '',
          },
          {
            id: OrderDetailId.DivisionCode,
            title: 'Division Code',
            testId: OrderDetailsTestIds.DivisionCodeItem,
            content: details?.divisions
              ? details?.divisions
                  .map(({ name, code }) => formatNameWithCode(name, code))
                  .join(',')
              : '',
          },
          {
            id: OrderDetailId.OrderDeliveryWindow,
            title: 'Order Delivery Window',
            testId: OrderDetailsTestIds.OrderDeliveryWindowItem,
            content: orderDeliveryWindowContent,
          },
          {
            id: OrderDetailId.Event,
            title: 'Event',
            testId: OrderDetailsTestIds.EventItem,
            content: formatNameWithCode(
              details.event?.name,
              details.event?.code,
            ),
          },
          {
            id: OrderDetailId.OrderType,
            title: 'Order Type',
            testId: OrderDetailsTestIds.OrderTypeItem,
            content: details?.types
              ? details?.types
                  .map(({ name, code }) => formatNameWithCode(name, code))
                  .join(',')
              : '',
          },
        ]
      : []

    return !isBrand
      ? completeDetailsList.filter(
          (detail) =>
            ![
              OrderDetailId.Warehouse,
              OrderDetailId.DivisionCode,
              OrderDetailId.Event,
              OrderDetailId.OrderType,
            ].includes(detail.id),
        )
      : completeDetailsList
  }, [details, isBrand, dateFormat])

  return (
    <Container>
      {viewModeDetailList.map((item) => (
        <DetailsListItem
          key={item.id}
          testId={item.testId}
          title={item.title}
          content={item.content}
        />
      ))}
    </Container>
  )
}

type Props = {
  details: OrderDetailsQuery
  isBrand: boolean
  dateFormat: string
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`
